import * as React from 'react';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {Lead as LeadFragment} from 'nutshell-graphql-types';

import {refetchForEvent} from '../../refetch-helpers';

import {useUpdateLead} from '../graphql/hooks';

type Result = {
    handleUpdateName: (name: string) => Promise<any>;
    handleUpdateConfidence: (confidence: number) => void;
    handleUpdateDueDate: (dueDate?: Date | null | undefined) => void;
    handleUpdateCreatedDate: (createdDate?: Date | null | undefined) => void;
    handleUpdateClosedDate: (closedDate?: Date | null | undefined) => void;
    handleUpdateTags: (tags: Tag[]) => void;
};

export function useUpdateLeadHandlers(lead?: LeadFragment | null): Result {
    const {updateLead} = useUpdateLead();

    const handleUpdateName = React.useCallback(
        (name: string) => {
            if (!lead) {
                return Promise.resolve();
            }

            return updateLead(
                {
                    id: lead.id,
                    description: name.length ? name : null,
                },
                lead
            );
        },
        [updateLead, lead]
    );

    const handleUpdateConfidence = React.useCallback(
        (confidence: number) => {
            if (!lead) {
                return;
            }

            updateLead(
                {
                    id: lead.id,
                    confidence,
                },
                lead
            );
        },
        [updateLead, lead]
    );

    const handleUpdateDueDate = React.useCallback(
        (dueDate?: Date | null) => {
            if (!lead) {
                return;
            }

            updateLead(
                {
                    id: lead.id,
                    dueTime: dueDate ? dueDate.getTime() / 1000 : null,
                },
                lead
            ).then(() => {
                refetchForEvent('dueDateUpdated');
            });
        },
        [updateLead, lead]
    );

    const handleUpdateCreatedDate = React.useCallback(
        (createdDate?: Date | null) => {
            if (!lead) {
                return;
            }

            updateLead(
                {
                    id: lead.id,
                    createdTime: createdDate ? createdDate.getTime() / 1000 : null,
                },
                lead
            );
        },
        [updateLead, lead]
    );

    const handleUpdateClosedDate = React.useCallback(
        (closedDate?: Date | null) => {
            if (!lead) {
                return;
            }

            updateLead(
                {
                    id: lead.id,
                    closedTime: closedDate ? closedDate.getTime() / 1000 : null,
                },
                lead
            );
        },
        [updateLead, lead]
    );

    const handleUpdateTags = React.useCallback(
        (newTags: Tag[]) => {
            if (!lead) {
                return;
            }

            updateLead(
                {
                    id: lead.id,
                    // @ts-expect-error
                    tags: newTags,
                },
                lead
            );
        },
        [updateLead, lead]
    );

    return {
        handleUpdateName,
        handleUpdateConfidence,
        handleUpdateDueDate,
        handleUpdateCreatedDate,
        handleUpdateClosedDate,
        handleUpdateTags,
    };
}
